import type {
  SavingsFeature,
  SavingsPartner,
  SavingsPartnerConfigurationVersion,
  SavingsPartnerPayload
} from '@interfaces/Savings.interface';
import type { SavingsCustomerType } from '@interfaces/SavingsCustomer.interface';
import {
  SAVINGS_API_VERSION_HEADERS,
  SavingsReferenceResourceClient
} from '../savings-reference-resource';

const LOOKUP_PAGE_SIZE = 100;
const VERSION_PAGE_SIZE = 50;

export class ApiSavingsPartners extends SavingsReferenceResourceClient<
  SavingsPartner,
  SavingsPartnerPayload
> {
  protected readonly path = '/savings-partners';
  protected readonly searchFilter = 'company_name';

  /** Both lookups are short catalogues, so one large page holds every option. */
  private async lookup<T>(
    url: string,
    params: Record<string, string | number> = {}
  ): Promise<T[]> {
    const res = await this.client.get(url, {
      headers: SAVINGS_API_VERSION_HEADERS,
      params: { 'page[size]': LOOKUP_PAGE_SIZE, ...params }
    });
    return res.data.data;
  }

  async get(id: number): Promise<SavingsPartner> {
    const res = await this.client.get(`${this.path}/${id}`, {
      headers: SAVINGS_API_VERSION_HEADERS
    });
    return res.data.data;
  }

  features(): Promise<SavingsFeature[]> {
    return this.lookup('/savings-features');
  }

  /** Partners may offer types that are closed to direct Hub registration. */
  customerTypes(): Promise<SavingsCustomerType[]> {
    return this.lookup('/savings-customer-types', {
      include_registration_disabled: 'true'
    });
  }

  /** The most recent versions, newest first. */
  async versions(
    companyId: number,
    configurationId: number
  ): Promise<SavingsPartnerConfigurationVersion[]> {
    return this.lookup(this.versionsPath(companyId, configurationId), {
      'page[size]': VERSION_PAGE_SIZE
    });
  }

  /** Saves an old version's settings again; the API records it as a new version. */
  async restoreVersion(
    companyId: number,
    configurationId: number,
    version: number
  ): Promise<SavingsPartner> {
    const res = await this.client.post(
      `${this.versionsPath(companyId, configurationId)}/${version}/restore`,
      {},
      { headers: SAVINGS_API_VERSION_HEADERS }
    );
    return res.data.data;
  }

  private versionsPath(companyId: number, configurationId: number): string {
    return `${this.path}/${companyId}/configurations/${configurationId}/versions`;
  }

  async create(payload: SavingsPartnerPayload): Promise<SavingsPartner> {
    const res = await this.client.post(this.path, ...this.requestBody(payload));
    return res.data.data;
  }

  /** PUT is the documented edit; a logo upload tunnels it through POST with `_method`. */
  async update(
    id: number,
    payload: Partial<SavingsPartnerPayload>
  ): Promise<SavingsPartner> {
    const url = `${this.path}/${id}`;
    const res = payload.logo
      ? await this.client.post(url, ...this.requestBody(payload, 'PUT'))
      : await this.client.put(url, payload, {
          headers: SAVINGS_API_VERSION_HEADERS
        });
    return res.data.data;
  }

  private requestBody(
    { logo, ...payload }: Partial<SavingsPartnerPayload>,
    method?: 'PUT'
  ) {
    if (!logo) {
      return [payload, { headers: SAVINGS_API_VERSION_HEADERS }] as const;
    }

    const form = new FormData();
    if (method) form.append('_method', method);
    if (payload.company_id !== undefined) {
      form.append('company_id', String(payload.company_id));
    }
    form.append('configuration', JSON.stringify(payload.configuration ?? {}));
    form.append('logo', logo);

    return [
      form,
      {
        headers: {
          ...SAVINGS_API_VERSION_HEADERS,
          'Content-Type': 'multipart/form-data'
        }
      }
    ] as const;
  }
}

export { savingsPartnersQueryKeys } from './query-keys';
export {
  useSavingsFeaturesQuery,
  useSavingsPartnerConfigurationVersionsQuery,
  useSavingsPartnerCustomerTypesQuery,
  useSavingsPartnerQuery,
  useSavingsPartnersListQuery
} from './use-savings-partners-queries';
export {
  useSavingsPartnerRestoreVersionMutation,
  useSavingsPartnersCreateMutation,
  useSavingsPartnersDeleteMutation,
  useSavingsPartnersUpdateMutation
} from './use-savings-partners-mutations';
export type { SavingsPartnersUpdateVariables } from './use-savings-partners-mutations';
